import {
  getSystemCoolingMaxTemp,
  getSystemHeatingMinTemp
} from 'components/equipment-page/equipment-comparison-utils';
import { SystemOptionType } from 'data/types';
import { formatCoverageScore } from 'utils/coverage-score-format';
import {
  CoverageShortfall,
  getCondenserShortfallFigures,
  getCoverageShortfalls,
  INSUFFICIENT_COVERAGE_THRESHOLD_PERCENT,
  isInsufficientCoverage
} from 'utils/coverage-shortfalls';
import {
  DuctedCoverageHeadline,
  ductedCoverageHeadlinesForSystem,
  ductedHoursShareLabel
} from 'utils/ducted-performance-display';
import { isDuctedCompatibilitySystem } from 'utils/ducted-quote-layout';
import { requiredRatioOnOption } from 'utils/required-share-ratio';
import { shareCoverageScoreOnOption } from 'utils/share-coverage';

export type CoverageWarningSide = 'heating' | 'cooling';

export type InsufficientCoverageWarning = {
  side: CoverageWarningSide;
  headline: string;
  explanation: string;
  details: CoverageShortfall[];
};

export type SystemCoverageWarnings = {
  key: string;
  name: string;
  warnings: InsufficientCoverageWarning[];
};

type WarningCopy = {
  label: string;
  supplement: string;
  fullCoverage: string;
  extremeDays: string;
  beyondTemp: (temp: number) => string;
};

const WARNING_COPY: Record<CoverageWarningSide, WarningCopy> = {
  heating: {
    label: 'Heating',
    supplement: 'Backup or supplemental heating',
    fullCoverage: 'full heating coverage',
    extremeDays: 'on the coldest days',
    beyondTemp: (temp) => `when it's colder than ${Math.round(temp)}°F`
  },
  cooling: {
    label: 'Cooling',
    supplement: 'Backup or supplemental cooling',
    fullCoverage: 'full cooling coverage',
    extremeDays: 'on the hottest days',
    beyondTemp: (temp) => `when it's hotter than ${Math.round(temp)}°F`
  }
};

const SIDES: CoverageWarningSide[] = ['heating', 'cooling'];

function explanationSentence(
  copy: WarningCopy,
  duration: string,
  tail: string = '',
  existingEquipmentCoversRest: boolean = false
): string {
  if (existingEquipmentCoversRest) {
    return `Your existing ${copy.label.toLowerCase()} covers the remaining hours ${duration}${tail}.`;
  }
  return `${copy.supplement} will be needed ${duration} to get ${copy.fullCoverage} from this system${tail}.`;
}

function fallbackDuration(copy: WarningCopy, temp: number | null): string {
  return temp === null ? copy.extremeDays : copy.beyondTemp(temp);
}

function withTempClause(
  copy: WarningCopy,
  duration: string,
  temp: number | null
): string {
  return temp === null ? duration : `${duration} (${copy.beyondTemp(temp)})`;
}

function ductlessScore(system: SystemOptionType, side: CoverageWarningSide) {
  return side === 'heating' ? system.heatingScore : system.coolingScore;
}

function ductlessTemp(system: SystemOptionType, side: CoverageWarningSide) {
  return side === 'heating'
    ? getSystemHeatingMinTemp(system)
    : getSystemCoolingMaxTemp(system);
}

function ductlessWarning(
  system: SystemOptionType,
  side: CoverageWarningSide
): InsufficientCoverageWarning | null {
  const score = ductlessScore(system, side);
  const requiredRatio = requiredRatioOnOption(system, side);
  const existingEquipmentCoversRest = requiredRatio < 1;
  // The share coverage is capped per temperature bin, so it is judged
  // against the plain threshold; options without it fall back to scaling
  // the whole-room threshold by the share.
  const shareScore = shareCoverageScoreOnOption(system, side);
  const insufficient =
    shareScore !== null
      ? isInsufficientCoverage(shareScore)
      : isInsufficientCoverage(
          score,
          INSUFFICIENT_COVERAGE_THRESHOLD_PERCENT * requiredRatio
        );
  if (!insufficient || score === null) {
    return null;
  }

  const copy = WARNING_COPY[side];
  const headline = `${copy.label} coverage: ${formatCoverageScore(score)}`;
  const temp = ductlessTemp(system, side);
  const { condenserCoverage } = system;
  const shortfalls = getCoverageShortfalls(side, score, condenserCoverage);

  if (shortfalls.length === 0) {
    return {
      side,
      headline,
      explanation: explanationSentence(
        copy,
        fallbackDuration(copy, temp),
        '',
        existingEquipmentCoversRest
      ),
      details: []
    };
  }

  if (condenserCoverage.length > 1) {
    const lead = existingEquipmentCoversRest
      ? `Your existing ${copy.label.toLowerCase()} covers the remaining hours:`
      : `${copy.supplement} will be needed to get ${copy.fullCoverage} from this system:`;
    return {
      side,
      headline,
      explanation: lead,
      details: shortfalls
    };
  }

  const { insufficientTimePercent, averagePartialCoveragePercent } =
    getCondenserShortfallFigures(condenserCoverage[0], side);
  return {
    side,
    headline,
    explanation: explanationSentence(
      copy,
      withTempClause(
        copy,
        `about ${insufficientTimePercent}% of the time`,
        temp
      ),
      ` — it averages ${averagePartialCoveragePercent}% coverage during that time`,
      existingEquipmentCoversRest
    ),
    details: []
  };
}

function ductedHoursDuration(
  side: CoverageWarningSide,
  hoursPercent: number
): string {
  if (100 - hoursPercent < 1) {
    return `for less than ${ductedHoursShareLabel(side, 1)}`;
  }
  return `for about ${ductedHoursShareLabel(
    side,
    100 - Math.floor(hoursPercent)
  )}`;
}

function ductedWarning(
  side: CoverageWarningSide,
  headline: DuctedCoverageHeadline
): InsufficientCoverageWarning | null {
  if (!isInsufficientCoverage(headline.percent)) return null;

  const copy = WARNING_COPY[side];
  const { hoursPercent, fullOutputTemp } = headline;
  const duration =
    hoursPercent === null
      ? fallbackDuration(copy, fullOutputTemp)
      : withTempClause(
          copy,
          ductedHoursDuration(side, hoursPercent),
          fullOutputTemp
        );

  return {
    side,
    headline: `${copy.label} coverage: ${headline.label}`,
    explanation: explanationSentence(copy, duration),
    details: []
  };
}

function presentWarnings(
  warnings: (InsufficientCoverageWarning | null)[]
): InsufficientCoverageWarning[] {
  return warnings.filter(
    (warning): warning is InsufficientCoverageWarning => warning !== null
  );
}

export function getInsufficientCoverageWarnings(
  system: SystemOptionType
): InsufficientCoverageWarning[] {
  const ducted = ductedCoverageHeadlinesForSystem(system);
  if (ducted) {
    return presentWarnings(
      SIDES.map((side) => ductedWarning(side, ducted[side]))
    );
  }
  if (isDuctedCompatibilitySystem(system)) {
    return [];
  }
  return presentWarnings(SIDES.map((side) => ductlessWarning(system, side)));
}

export function collectInsufficientCoverageWarnings(
  systems: SystemOptionType[]
): SystemCoverageWarnings[] {
  return systems
    .map((system) => ({
      key: system.id,
      name: system.name,
      warnings: getInsufficientCoverageWarnings(system)
    }))
    .filter((entry) => entry.warnings.length > 0);
}
